import {Injectable} from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {
  podValidationBasicSearchCriteriaToQueryParams,
  PodValidationSearchCriteria,
  podValidationSearchCriteriaToQueryParams
} from './pod-validation-search-criteria.model';
import {Observable} from 'rxjs';
import {PodValidation} from './pod-validation.model';
import {SearchPodValidationResult} from './search-pod-validation-result.model';
import {HttpUtils} from '../../shared/http-utils';
import {URL_BASE} from '../../core/constants';
import {PodValidationDocument} from './pod-validation-document.model';
import {map, switchMap} from 'rxjs/operators';
import {FileService} from '../../shared/services/file.service';
import {AuthenticationService} from '../../shared/services/authentication.service';
import {MimeTypeUtils} from '../../shared/mime-type-utils';

@Injectable()
export class PodValidationService {

  private PODS_VALIDATION_URI = 'pods-validation';

  public static getPodValidationAcceptedTypes(): string[] {
    return [
        MimeTypeUtils.getMimeTypeByExtension('png'),
        MimeTypeUtils.getMimeTypeByExtension('jpeg'),
        MimeTypeUtils.getMimeTypeByExtension('pdf')
      ]
  }

  constructor(private http: HttpClient, private fileService: FileService, private authService: AuthenticationService) {
  }

  public search(filters: PodValidationSearchCriteria): Observable<SearchPodValidationResult> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/search`;
    const params = this.getSearchParamsFromCriteria(filters);

    return this.http.get(url, { headers, params }).pipe(
      map(result => SearchPodValidationResult.fromDto(result))
    );
  }

  public getById(podValidationId: string): Observable<PodValidation> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/${podValidationId}`;

    return this.http.get(url, { headers }).pipe(
      map(result => PodValidation.fromDto(result))
    );
  }

  public export(filters: PodValidationSearchCriteria): Observable<void> {
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/export`;
    const headers = HttpUtils.createHeaders();
    const params = this.getSearchParamsFromCriteria({... filters, podState: null});

    return this.http.get(url, { headers, params, responseType: 'blob', observe: 'response' })
      .pipe(switchMap((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response)));
  }

  public getPodDocument(podValidation: PodValidation): Observable<PodValidationDocument> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/${podValidation.id}/document`;

    return this.http.get(url, { headers, responseType: 'blob', observe: 'response' }).pipe(
      map((response: HttpResponse<Blob>) => PodValidationDocument.fromDto(response))
    );
  }

  public validate(podValidation: PodValidation): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/${podValidation.id}/validate`;

    return this.http.put<void>(url, null, { headers });
  }

  public reject(podValidation: PodValidation, reason: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/${podValidation.id}/reject`;

    return this.http.put<void>(url, {reason}, { headers });
  }

  public upload(podValidation: PodValidation, file: File): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/pod/document`;
    const formData = new FormData();
    formData.append('params', new Blob([JSON.stringify(podValidation)], {type: 'application/json'}));
    formData.append('file', file, file.name);

    return this.http.post<void>(url, formData, { headers });
  }

  public sendPodPendingReminder(carrierId: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/carriers/${carrierId}/remind`;

    return this.http.put<void>(url, {}, { headers });
  }

  public updateProducts(podValidation: PodValidation): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/${this.PODS_VALIDATION_URI}/${this.getRoleSpecificUriPart()}/pod/products`;

    return this.http.put<void>(url, podValidation, { headers });
  }

  private getSearchParamsFromCriteria(searchCriteria: PodValidationSearchCriteria): HttpParams {
    if (this.authService.canManagePodsValidation() ) {
      return podValidationSearchCriteriaToQueryParams(searchCriteria);
    } else {
      return podValidationBasicSearchCriteriaToQueryParams(searchCriteria);
    }
  }

  private getRoleSpecificUriPart() {
    if (this.authService.canManagePodsValidation() ) {
     return 'pod-reviewer';
    } else {
      return 'carrier-manager';
    }
  }

}
